const BACKEND = "https://minitest.sandrone.org/api";
const tokens: { marketingId: null | string; jwt: null | string; jwtExpires: null | number } = {
    marketingId: null,
    jwt: null,
    jwtExpires: null,
};

async function getToken(): Promise<string> {
    if (!tokens.jwt || !tokens.jwtExpires || Date.now() >= tokens.jwtExpires) {
        tokens.jwt = (
            await (
                await fetch(`${BACKEND}/account/token`, {
                    method: "POST",
                    body: tokens.marketingId,
                })
            ).json()
        ).token as string;
        tokens.jwtExpires = Date.now() + 60 * 60 * 1000;
    }

    return tokens.jwt;
}

export interface AccountProperties {
    editor_activated: boolean;
    kromer: number;
}

async function getMyAccount(): Promise<AccountProperties> {
    return (await (
        await fetch(`${BACKEND}/account/me`, {
            headers: {
                Authorization: `Bearer ${await getToken()}`,
            },
        })
    ).json()) as AccountProperties;
}

async function buyKromer(amount: number, signature: string): Promise<void> {
    let response = await fetch(`${BACKEND}/account/pay`, {
        method: "PUT",
        headers: {
            Authorization: `Bearer ${await getToken()}`,
        },
        body: JSON.stringify({ amount, signature }),
    });

    if (!response.ok) {
        throw new Error((await response.json())["error"]);
    }
}

async function activateEditor(password: string): Promise<void> {
    let response = await fetch(`${BACKEND}/account/editor`, {
        method: "PUT",
        headers: {
            Authorization: `Bearer ${await getToken()}`,
        },
        body: password,
    });

    if (!response.ok) {
        throw new Error((await response.json())["error"]);
    }
}

async function claimGift(): Promise<boolean> {
    let response = await fetch(`${BACKEND}/account/gift`, {
        method: "POST",
        headers: {
            Authorization: `Bearer ${await getToken()}`,
        },
    });

    return response.ok;
}

/* null to retain the last uploaded mewun */
type UpdateBlob = Blob | null;

interface TestAuthorInfo {
    id: number;
    name: string;
    icon: string;
}

export interface TestQuestion {
    id: string;
    title: string;
    cover: string | UpdateBlob;
    options: { [key: string]: string };
}

export interface TestResult {
    id: string;
    title: string;
    cover: string | UpdateBlob;
    caption: string;
    options: { [question: string]: string | null };
}

export interface Test {
    id: string;
    icon: string | UpdateBlob;
    cover: string | UpdateBlob;
    title: string;
    description: string;
    price: number | null;
    author: TestAuthorInfo | null;
    questions: TestQuestion[];
    results: TestResult[];
    dirty?: boolean;
    draft: boolean;
}

export type TestPreview = Omit<Omit<Test, "questions">, "results">;

type TestListId = "popular" | "new" | "my" | "drafts";

export interface Collection<T> {
    thisOffset: string;
    nextOffset: string;
    hasMore: boolean;
    list: T[];
}

async function getTests(
    collection: TestListId,
    offset: string = "0",
): Promise<Collection<TestPreview>> {
    let response = await fetch(`${BACKEND}/tests/${collection}`, {
        method: "POST",
        headers: {
            Authorization: `Bearer ${await getToken()}`,
        },
        body: JSON.stringify({ offset }),
    });

    let bruh = !response.ok;
    let maybeCollection = await response.json();
    if (bruh) throw new Error(maybeCollection["error"]);

    return maybeCollection;
}

async function searchImages(
    query: string,
    offset: string = "0",
): Promise<Collection<{ alt: string; url: string }>> {
    let response = await fetch(
        `${BACKEND}/images/search?q=${encodeURIComponent(query)}&offset=${offset}`,
        {
            method: "GET",
            headers: {
                Authorization: `Bearer ${await getToken()}`,
            },
        },
    );

    let bruh = !response.ok;
    let maybeCollection = await response.json();
    if (bruh) throw new Error(maybeCollection["error"]);

    return maybeCollection;
}

async function getTest(
    id: string,
    drafts: boolean,
    spendKromer: boolean,
    preview: boolean = false,
): Promise<Test> {
    let response = await fetch(
        `${BACKEND}/tests/${drafts ? "drafts/" : ""}${id}${preview ? "-truncated" : ""}.json`,
        {
            method: "POST",
            headers: {
                Authorization: `Bearer ${await getToken()}`,
            },
            body: JSON.stringify({ spendKromer }),
        },
    );

    let bruh = !response.ok;
    let test = await response.json();
    if (bruh) throw new Error(test["error"]);

    return test;
}

async function removeTest(id: string, draft: boolean): Promise<void> {
    let response = await fetch(`${BACKEND}/tests/${draft ? "drafts/" : ""}${id}.json`, {
        method: "DELETE",
        headers: {
            Authorization: `Bearer ${await getToken()}`,
        },
    });

    if (!response.ok) {
        throw new Error((await response.json())["error"]);
    }
}

async function syncTest(id: string): Promise<void> {
    let response = await fetch(`${BACKEND}/tests/drafts/${id}.json/do=sync`, {
        method: "POST",
        headers: {
            Authorization: `Bearer ${await getToken()}`,
        },
    });

    if (!response.ok) {
        throw new Error((await response.json())["error"]);
    }
}

async function syncTestReverse(id: string): Promise<void> {
    let response = await fetch(`${BACKEND}/tests/${id}.json/do=sync`, {
        method: "POST",
        headers: {
            Authorization: `Bearer ${await getToken()}`,
        },
    });

    if (!response.ok) {
        throw new Error((await response.json())["error"]);
    }
}

function testToFormData(test: Test): FormData {
    let blobCount = 0;
    let formData = new FormData();
    const visitEntry = (obj: { [key: string]: any }, key: string): void => {
        const maybeBlob: string | UpdateBlob = obj[key];
        if (null == maybeBlob) return;

        if (typeof maybeBlob === "string") {
            obj[key] = ":skip";
            return;
        }

        let id = `pic${blobCount++}`;
        formData.append(id, maybeBlob);
        obj[key] = `:${id}`;
    };

    visitEntry(test, "icon");
    visitEntry(test, "cover");
    Object.values(test.questions).forEach((question) => visitEntry(question, "cover"));
    Object.values(test.results).forEach((result) => visitEntry(result, "cover"));

    formData.append("test", JSON.stringify(test));

    return formData;
}

async function saveTest(test: Test): Promise<string> {
    let response = await fetch(
        `${BACKEND}/tests/drafts/${test.id == "" ? "new" : `${test.id}.json`}`,
        {
            method: "PUT",
            headers: {
                Authorization: `Bearer ${await getToken()}`,
            },
            body: testToFormData(test),
        },
    );

    let bruh = !response.ok;
    let newTest = await response.json();
    if (bruh) throw new Error(newTest["error"]);

    return newTest.id;
}

async function getResultShareablePictureRef(test: string, result: string): Promise<string> {
    let response = await (
        await fetch(`${BACKEND}/tests/results/GetShareablePictureRef.cfml?t=${test}&r=${result}`, {
            method: "GET",
            headers: {
                Authorization: `Bearer ${await getToken()}`,
            },
        })
    ).json();

    if (typeof response["error"] != "undefined") {
        throw new Error(response["error"]);
    }

    return response["ref"];
}

export default async function useApi() {
    if (!tokens.marketingId) {
        // @ts-ignore
        tokens.marketingId = (await VKAPI.getUser()).user.marketing_id;
        await getToken();
    }

    return {
        getResultShareablePictureRef,
        syncTestReverse,
        activateEditor,
        searchImages,
        getMyAccount,
        removeTest,
        claimGift,
        buyKromer,
        saveTest,
        syncTest,
        getTests,
        getTest,
    };
}
